/**
 * LayerDropZone — drag-and-drop pre-flight wrapper component.
 *
 * Spec: LAYER_LIFECYCLE_AND_PORTABLE_LAYER_AUDIT.md §10 (drop surface)
 * Phase: LAYER-LIFECYCLE-ALIGNMENT Phase E
 *
 * Responsibilities:
 *   • Detect drop source class (file / URL / cross-tab scene-tree row).
 *   • Read the first 4 KB of a file via FileReader for magic-byte sniffing.
 *   • Call `sniffArtifact()` to identify the artifact kind.
 *   • Look up `ARTIFACT_SCENE_CONTRACT` to determine slot / layerKind.
 *   • Compute a stub budget status against DEC-20-05 thresholds.
 *   • Call `previewAlignment` (Phase D stub or inline mock).
 *   • Build a `PreflightInspection` and forward it to `onPreflight`.
 *
 * NO commit happens here. This is a pure pre-flight inspection surface.
 *
 * Token-driven styling only — no raw hex, no raw pixel values.
 *
 * TODO (keyboard): implement keyboard-fallback file picker ("Add layer…"
 *   shortcut `?` on focused tree) as a follow-up in Phase E2.
 */

import {
  type DragEvent,
  type ReactNode,
  useCallback,
  useRef,
  useState,
} from 'react';

import { ARTIFACT_SCENE_CONTRACT } from '@/sdk-stub/gtwin-sdk';
import type { SceneSlot } from '@/sdk-stub/gtwin-sdk';
import { tokens } from '@/design';

import { sniffArtifact } from './sniffArtifactKind';
import { extractGeoJsonMetadata } from './extractGeoJsonMetadata';
import {
  groupDroppedFiles,
  missingRequiredShapefileSidecars,
} from './shapefileDropBundle';
import type {
  ArtifactKind,
  BudgetStatus,
  DropSource,
  PreflightBudget,
  PreflightInspection,
} from './preflightTypes';

// ─── ULID-lite stub ──────────────────────────────────────────────────────────
// TODO: replace with a proper ULID library or import from a shared util once
// the project adds one. This is a monotonic random-ID adequate for pre-flight
// event correlation and does not need cryptographic strength.

function generateUlid(): string {
  const ts = Date.now().toString(36).toUpperCase().padStart(10, '0');
  const rand = Math.random().toString(36).slice(2, 18).toUpperCase().padStart(16, '0');
  return (ts + rand).slice(0, 26);
}

// ─── Budget thresholds (DEC-20-05 + §10.3) ──────────────────────────────────

const BUDGET_WARN_SIZE_MB = 256;
const BUDGET_HARD_SIZE_MB = 2048;
const BUDGET_WARN_TILE_COUNT = 3_000_000;
const BUDGET_HARD_TILE_COUNT = 4_000_000;

function computeBudgetStatus(sizeMB?: number, tileEstimate?: number): BudgetStatus {
  if (sizeMB !== undefined) {
    if (sizeMB >= BUDGET_HARD_SIZE_MB) return 'exceed';
    if (sizeMB >= BUDGET_WARN_SIZE_MB) return 'warn';
  }
  if (tileEstimate !== undefined) {
    if (tileEstimate >= BUDGET_HARD_TILE_COUNT) return 'exceed';
    if (tileEstimate >= BUDGET_WARN_TILE_COUNT) return 'warn';
  }
  return 'ok';
}

function buildBudget(sizeMB?: number): PreflightBudget {
  const status = computeBudgetStatus(sizeMB);
  let details = 'Within budget limits';
  if (sizeMB !== undefined) {
    details = `${sizeMB.toFixed(1)} MB`;
    if (status === 'warn') details += ` — approaching ${BUDGET_WARN_SIZE_MB} MB warn threshold`;
    if (status === 'exceed') details += ` — exceeds ${BUDGET_HARD_SIZE_MB} MB hard cap`;
  }
  return { sizeMB, status, details };
}

// ─── Alignment preview stub ──────────────────────────────────────────────────
// TODO (Phase D integration): replace this inline mock with the real
// `previewAlignment` call once `POST /api/v1/projects/{id}/align/preview`
// (Phase B2) is available and the Phase D CRS Reconciliation Wizard exports
// a `previewAlignment` function. For now, a local mock returns `identity`
// for all known-good artifact kinds and `reject` when CRS is absent.

type AlignDirective = 'identity' | 'reproject' | 'reject';

interface AlignResult {
  directive: AlignDirective;
  reason?: string;
}

function previewAlignmentStub(kind: ArtifactKind, hasCrs: boolean): AlignResult {
  // engineeringModels and overlays are always Identity per align_for_pin rules.
  const contract = ARTIFACT_SCENE_CONTRACT[kind as keyof typeof ARTIFACT_SCENE_CONTRACT];
  if (!contract) return { directive: 'identity' };
  if (contract.slot === 'engineeringModels' || contract.slot === 'overlays') {
    return { directive: 'identity' };
  }
  // Files that require conversion cannot be aligned yet.
  if (kind === 'ifc-glb' || kind === 'e57' || kind === 'las') {
    return { directive: 'identity' }; // will be re-evaluated after conversion
  }
  if (!hasCrs) {
    return { directive: 'reject', reason: 'CV_E_CRS_MISSING' };
  }
  return { directive: 'identity' };
}

// ─── Slot inference ──────────────────────────────────────────────────────────

const TARGET_KIND_DEFAULT_SLOT: Record<
  'scene-tree-node' | 'viewport' | 'add-layer-button',
  SceneSlot
> = {
  'scene-tree-node': 'realityData',
  'viewport': 'realityData',
  'add-layer-button': 'realityData',
};

function inferSlot(kind: ArtifactKind, targetKind: LayerDropZoneProps['targetKind']): SceneSlot {
  const contract = ARTIFACT_SCENE_CONTRACT[kind as keyof typeof ARTIFACT_SCENE_CONTRACT];
  if (contract) return contract.slot as SceneSlot;
  return TARGET_KIND_DEFAULT_SLOT[targetKind];
}

function inferLayerKind(kind: ArtifactKind): string {
  const contract = ARTIFACT_SCENE_CONTRACT[kind as keyof typeof ARTIFACT_SCENE_CONTRACT];
  return contract?.layerKind ?? 'unknown';
}

// ─── Recommended action ──────────────────────────────────────────────────────

function buildRecommendedAction(
  kind: ArtifactKind,
  alignDirective: AlignDirective,
  budgetStatus: BudgetStatus,
  source: DropSource,
): PreflightInspection['recommendedAction'] {
  if (kind === 'glayer') return 'import-glayer';
  if (budgetStatus === 'exceed') return 'reject';
  if (alignDirective === 'reject') return 'reject';
  const convertFirstKinds: ArtifactKind[] = [
    'ifc-glb',
    'e57',
    'las',
    'laz',
    'geojson',
    'shp',
    'flatgeobuf',
    'geoparquet',
    'dxf',
  ];
  if (source === 'file' && convertFirstKinds.includes(kind)) {
    return 'convert-first';
  }
  if (alignDirective === 'reproject') return 'reconcile-crs';
  return 'pin';
}

// ─── File reader helper ──────────────────────────────────────────────────────

const MAGIC_BYTE_READ_SIZE = 4096; // 4 KB

function readFirstBytes(file: File): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const buffer = reader.result as ArrayBuffer;
      resolve(new Uint8Array(buffer));
    };
    reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'));
    reader.readAsArrayBuffer(file.slice(0, MAGIC_BYTE_READ_SIZE));
  });
}

// ─── Custom MIME for cross-tab scene-tree drops ───────────────────────────────

const SCENE_ITEM_REF_MIME = 'application/x-geosoft-scene-item-ref';

// ─── Component props ─────────────────────────────────────────────────────────

export interface LayerDropZoneProps {
  /** Content to wrap with the drop target. */
  children: ReactNode;
  /**
   * Drives default slot selection when the dropped kind is not in the
   * `ARTIFACT_SCENE_CONTRACT`.
   */
  targetKind: 'scene-tree-node' | 'viewport' | 'add-layer-button';
  /**
   * When provided, overrides the slot inferred from the artifact kind.
   * Used when the user drops onto a specific scene-tree row (e.g. the
   * `engineeringModels` slot header).
   */
  slot?: SceneSlot;
  /** When `true`, the drop zone accepts no events. */
  disabled?: boolean;
  /** Called once per dropped file/URL with its pre-flight inspection. */
  onPreflight: (inspection: PreflightInspection) => void;
  /** Called when a recoverable error occurs (e.g. FileReader failure). */
  onError: (error: Error) => void;
}

// ─── Component ───────────────────────────────────────────────────────────────

/**
 * Token-driven highlight class names.
 * All visual tokens are resolved via CSS custom properties from the platform
 * design system; no raw color values appear here.
 */
const STYLE = {
  wrapper: {
    position: 'relative' as const,
  },
  overlay: {
    position: 'absolute' as const,
    inset: 0,
    pointerEvents: 'none' as const,
    borderRadius: 'var(--shape-corner-small)',
    outline: `${tokens.border.width.thin * 2}px solid var(--color-primary)`,
    outlineOffset: '-2px',
    backgroundColor: 'color-mix(in srgb, var(--color-primary) calc(var(--state-hover-opacity) * 100%), transparent)',
    transition: 'opacity var(--motion-duration-short3) var(--motion-easing-standard)',
  } as React.CSSProperties,
};

export function LayerDropZone({
  children,
  targetKind,
  slot: slotProp,
  disabled = false,
  onPreflight,
  onError,
}: LayerDropZoneProps) {
  const [isDragOver, setIsDragOver] = useState(false);
  // Track drag-enter depth to avoid false leave events from child elements.
  const enterDepthRef = useRef(0);

  const handleDragEnter = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      if (disabled) return;
      event.preventDefault();
      enterDepthRef.current += 1;
      setIsDragOver(true);
    },
    [disabled],
  );

  const handleDragOver = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      if (disabled) return;
      // Must call preventDefault to allow drops.
      event.preventDefault();
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = 'copy';
      }
    },
    [disabled],
  );

  const handleDragLeave = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      if (disabled) return;
      event.preventDefault();
      enterDepthRef.current = Math.max(0, enterDepthRef.current - 1);
      if (enterDepthRef.current === 0) {
        setIsDragOver(false);
      }
    },
    [disabled],
  );

  const handleDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      if (disabled) return;
      event.preventDefault();
      enterDepthRef.current = 0;
      setIsDragOver(false);

      const dt = event.dataTransfer;
      if (!dt) return;

      // ── Detect source class ──────────────────────────────────────────────

      // Cross-tab scene-tree row (application/x-geosoft-scene-item-ref)
      const sceneItemRef = dt.getData(SCENE_ITEM_REF_MIME);
      if (sceneItemRef) {
        const inspection = buildSceneItemRefInspection(sceneItemRef, targetKind, slotProp);
        onPreflight(inspection);
        return;
      }

      // URL drop (text/uri-list)
      const uriList = dt.getData('text/uri-list');
      if (uriList && dt.files.length === 0) {
        const urls = uriList
          .split(/\r?\n/)
          .map((line) => line.trim())
          .filter((line) => line.length > 0 && !line.startsWith('#'));

        for (const url of urls) {
          const kind = sniffArtifact({ url });
          const resolvedSlot = slotProp ?? inferSlot(kind, targetKind);
          const inspection = buildInspection({
            id: generateUlid(),
            source: 'url',
            url,
            kind,
            slot: resolvedSlot,
            sizeMB: undefined,
          });
          onPreflight(inspection);
        }
        return;
      }

      // File drops
      if (dt.files.length > 0) {
        void processFiles(
          Array.from(dt.files),
          targetKind,
          slotProp,
          onPreflight,
          onError,
        );
      }
    },
    [disabled, targetKind, slotProp, onPreflight, onError],
  );

  return (
    <div
      style={STYLE.wrapper}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      aria-dropeffect={disabled ? 'none' : 'copy'}
    >
      {children}
      {/* Token-driven highlight overlay — only mounted when dragging over. */}
      {isDragOver && !disabled && (
        <div
          style={STYLE.overlay}
          aria-hidden="true"
          role="presentation"
        />
      )}
    </div>
  );
}

// ─── Inspection builders ─────────────────────────────────────────────────────

interface BuildInspectionParams {
  id: string;
  source: DropSource;
  fileName?: string;
  fileSize?: number;
  url?: string;
  kind: ArtifactKind;
  slot: SceneSlot;
  sizeMB: number | undefined;
  crs?: PreflightInspection['crs'];
  bbox?: number[];
  droppedFile?: File;
  droppedSidecars?: readonly File[];
}

function buildInspection(params: BuildInspectionParams): PreflightInspection {
  const {
    id,
    source,
    fileName,
    fileSize,
    url,
    kind,
    slot,
    sizeMB,
    crs,
    bbox,
    droppedFile,
    droppedSidecars,
  } = params;

  const budget = buildBudget(sizeMB);
  const alignResult = previewAlignmentStub(kind, Boolean(crs?.code));
  const warnings: string[] = [];

  if (kind === 'unknown') {
    warnings.push('Artifact kind could not be detected. Conversion may be required.');
  }
  if (kind === 'ifc-glb') {
    warnings.push('IFC files require an ad-hoc converter run before they can be pinned.');
  }
  if (kind === 'e57') {
    warnings.push('E57 files require an ad-hoc converter run before they can be pinned.');
  }
  if (kind === 'las' || kind === 'laz') {
    warnings.push('Raw LAS/LAZ files require conversion to COPC before pinning.');
  }
  if (source === 'file' && (kind === 'geojson' || kind === 'shp' || kind === 'flatgeobuf' || kind === 'dxf')) {
    warnings.push('Local vector files are converted to PMTiles before pinning.');
  }
  if (budget.status === 'warn') {
    warnings.push(`File size is approaching the ${BUDGET_WARN_SIZE_MB} MB warn threshold.`);
  }
  if (budget.status === 'exceed') {
    warnings.push(`File size exceeds the ${BUDGET_HARD_SIZE_MB} MB hard cap and cannot be pinned directly.`);
  }

  const recommendedAction = buildRecommendedAction(
    kind,
    alignResult.directive,
    budget.status,
    source,
  );

  return {
    id,
    source,
    fileName,
    fileSize,
    url,
    detectedKind: kind,
    detectedSlot: slot,
    detectedLayerKind: inferLayerKind(kind),
    crs,
    bbox,
    budget,
    alignDirective: alignResult.directive,
    recommendedAction,
    warnings,
    droppedFile,
    droppedSidecars,
  };
}

async function inspectDroppedFile(
  file: File,
  targetKind: LayerDropZoneProps['targetKind'],
  slotProp: SceneSlot | undefined,
  sidecars: readonly File[] = [],
): Promise<PreflightInspection> {
  let firstBytes: Uint8Array | undefined;
  try {
    firstBytes = await readFirstBytes(file);
  } catch {
    firstBytes = undefined;
  }

  const sizeMB = file.size / (1024 * 1024);
  const kind = sniffArtifact({
    filename: file.name,
    firstBytes,
    mime: file.type || undefined,
  });

  let crs: PreflightInspection['crs'];
  let bbox: number[] | undefined;
  if (kind === 'geojson' && file.size <= 8 * 1024 * 1024) {
    try {
      const text = await file.text();
      const meta = extractGeoJsonMetadata(text);
      crs = meta.crs;
      bbox = meta.bbox;
    } catch {
      // Best-effort — converter run still resolves CRS server-side.
    }
  }

  let resolvedSlot: SceneSlot = slotProp ?? inferSlot(kind, targetKind);
  if (kind === 'geojson' || kind === 'shp') {
    resolvedSlot = 'featureLayers';
  }

  const source: DropSource = kind === 'glayer' ? 'glayer' : 'file';
  const inspection = buildInspection({
    id: generateUlid(),
    source,
    fileName: file.name,
    fileSize: file.size,
    kind,
    slot: resolvedSlot,
    sizeMB,
    crs,
    bbox,
    droppedFile: source === 'file' ? file : undefined,
    droppedSidecars: sidecars.length > 0 ? sidecars : undefined,
  });

  if (kind === 'shp') {
    const missing = missingRequiredShapefileSidecars(file.name, sidecars);
    if (missing.length > 0) {
      inspection.warnings.push(
        `Missing shapefile sidecar(s): ${missing.join(', ')}. Drop .shp + .shx + .dbf together.`,
      );
    }
  }

  if (kind === 'shp' || kind === 'geojson') {
    inspection.detectedLayerKind = 'vector';
  }

  if (kind === 'cog' && !crs) {
    inspection.warnings.push(
      'Local raster will be uploaded and converted to COG before pin; CRS is read from the GeoTIFF.',
    );
    inspection.crs = { code: 'EPSG:4326', coordinateMode: 'georeferenced' };
    inspection.alignDirective = 'identity';
    inspection.recommendedAction = 'pin';
  }

  if (kind === 'glayer') {
    inspection.alignDirective = 'identity';
    inspection.recommendedAction = 'import-glayer';
    inspection.warnings.push('Portable .glayer bundle — imports layer metadata into this project.');
  }

  return inspection;
}

async function processFiles(
  files: File[],
  targetKind: LayerDropZoneProps['targetKind'],
  slotProp: SceneSlot | undefined,
  onPreflight: LayerDropZoneProps['onPreflight'],
  onError: LayerDropZoneProps['onError'],
): Promise<void> {
  const { shapefileBundles, standaloneFiles } = groupDroppedFiles(files);

  for (const bundle of shapefileBundles) {
    try {
      const inspection = await inspectDroppedFile(
        bundle.primary,
        targetKind,
        slotProp,
        bundle.sidecars,
      );
      onPreflight(inspection);
    } catch (err) {
      onError(err instanceof Error ? err : new Error(String(err)));
    }
  }

  for (const file of standaloneFiles) {
    try {
      const inspection = await inspectDroppedFile(file, targetKind, slotProp);
      onPreflight(inspection);
    } catch (err) {
      onError(err instanceof Error ? err : new Error(String(err)));
    }
  }
}

/** Build a pre-flight inspection for a cross-tab scene-tree row drop. */
function buildSceneItemRefInspection(
  sceneItemRefJson: string,
  targetKind: LayerDropZoneProps['targetKind'],
  slotProp: SceneSlot | undefined,
): PreflightInspection {
  // The JSON envelope: { tenantId, gtwinId, sceneItemId, version }
  let url: string | undefined;
  try {
    const parsed = JSON.parse(sceneItemRefJson) as { sceneItemId?: string };
    if (parsed.sceneItemId) {
      url = `scene-item-ref:${parsed.sceneItemId}`;
    }
  } catch {
    // Ignore parse failure — ref is still a valid cross-tab drop.
  }

  const kind: ArtifactKind = 'unknown'; // kind resolved server-side for cross-tab
  const slot: SceneSlot = slotProp ?? TARGET_KIND_DEFAULT_SLOT[targetKind];

  return buildInspection({
    id: generateUlid(),
    source: 'scene-tree-row',
    url,
    kind,
    slot,
    sizeMB: undefined,
  });
}
